import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const RECEIPT_ID_PATTERN = /^[a-f0-9]{32}$/;
const OPERATION_LABELS = new Set(['upload', 'copy', 'move', 'delete']);
const PUBLIC_STATUSES = new Set([
    'available',
    'complete',
    'completed',
    'pending',
    'running',
    'needs_review',
    'undoing',
    'undone',
    'failed',
    'expired',
]);
const INTERNAL_PENDING_STATUSES = new Set(['prepared', 'deleting', 'db_pending']);

const requireScope = ({ caseId, userId }) => {
    const normalizedCaseId = String(caseId ?? '').trim();
    const normalizedUserId = String(userId ?? '').trim();
    if (!/^\d+$/.test(normalizedCaseId) || !/^\d+$/.test(normalizedUserId)) {
        throw new Error('The current document workspace could not be verified.');
    }
    return { caseId: normalizedCaseId, userId: normalizedUserId };
};

const readPayload = async (response) => {
    const payload = await response.json().catch(() => null);
    // These calls can mutate stored documents. A generic 2xx response is not
    // proof that the server committed or resumed the requested operation.
    // Require the endpoint's explicit success acknowledgement so an empty,
    // proxied, or otherwise malformed response never becomes a false undo.
    if (!response.ok || !payload || payload.success !== true) {
        const error = new Error(
            payload?.message || payload?.error || 'Recent document actions are unavailable.'
        );
        error.status = response.status;
        error.code = payload?.code || payload?.error_code || '';
        throw error;
    }
    return payload;
};

const normalizeTimestamp = (value) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    return value.trim().slice(0, 64);
};

const normalizeCachedCaseZipDownloadUrl = (value, caseId, endpoint) => {
    const rawUrl = String(value ?? '').trim();
    if (!rawUrl) throw new Error('The cached case ZIP returned an invalid response.');

    let apiUrl;
    let downloadUrl;
    try {
        apiUrl = new URL(endpoint, window.location.origin);
        downloadUrl = new URL(rawUrl, apiUrl);
    } catch (_) {
        throw new Error('The cached case ZIP returned an invalid response.');
    }
    const downloadQueryKeys = [...downloadUrl.searchParams.keys()];
    if (downloadUrl.origin !== apiUrl.origin
        || downloadUrl.pathname !== apiUrl.pathname
        || downloadUrl.searchParams.get('case_zip_download') !== caseId
        || !/^zip_[a-f0-9]{32}$/.test(downloadUrl.searchParams.get('archive') || '')
        || downloadQueryKeys.length !== 2
        || downloadQueryKeys.some(key => !['case_zip_download', 'archive'].includes(key))
        || downloadUrl.username || downloadUrl.password || downloadUrl.hash) {
        throw new Error('The cached case ZIP returned an invalid response.');
    }
    return downloadUrl.toString();
};

export const normalizeDocumentOperation = (operation) => {
    if (!operation || typeof operation !== 'object' || Array.isArray(operation)) {
        throw new Error('Recent document actions returned an invalid item.');
    }

    const receiptId = String(operation.receipt_id ?? '').trim();
    const operationName = String(operation.operation ?? '').trim().toLowerCase();
    const receivedStatus = String(operation.status ?? '').trim().toLowerCase();
    const status = INTERNAL_PENDING_STATUSES.has(receivedStatus) ? 'pending' : receivedStatus;
    const objectCount = Number(operation.object_count ?? 0);
    const isHistoryOnlyUpload = operationName === 'upload';
    if (!RECEIPT_ID_PATTERN.test(receiptId)
        || !OPERATION_LABELS.has(operationName)
        || !PUBLIC_STATUSES.has(status)
        || (isHistoryOnlyUpload && !['complete', 'completed'].includes(status))
        || !Number.isSafeInteger(objectCount)
        || objectCount < 0) {
        throw new Error('Recent document actions returned an invalid item.');
    }

    // Deliberately allowlist public summary fields. Tokens, object keys, and
    // internal payloads must never reach component state or the DOM.
    return {
        receiptId,
        operation: operationName,
        objectCount,
        status,
        createdAt: normalizeTimestamp(operation.created_at),
        expiresAt: normalizeTimestamp(operation.expires_at),
        // Upload history is informational. Even a malformed or future server
        // response must never turn an upload row into an Undo/Resume control.
        canUndo: !isHistoryOnlyUpload && operation.can_undo === true,
        canResume: !isHistoryOnlyUpload && operation.can_resume === true,
        cleanupPending: !isHistoryOnlyUpload && operation.cleanup_pending === true,
        resumeLabel: operation.resume_label === 'Resume undo' || status === 'undoing'
            ? 'Resume undo'
            : 'Resume',
    };
};

const request = async (scope, body, options = {}) => {
    const verified = requireScope(scope);
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    const csrfToken = await ensureCaseDbCsrfToken({ expectedUserId: verified.userId });
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    if (!csrfToken) {
        const error = new Error('Your session could not be verified. Sign in again and retry.');
        error.status = 401;
        throw error;
    }

    const response = await (options.fetchImpl || fetch)(
        options.endpoint || caseDbApiEndpoint('s3_upload.php'),
        {
            method: 'PUT',
            credentials: 'include',
            cache: 'no-store',
            signal: options.signal,
            headers: withCsrfHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }, 'PUT'),
            body: JSON.stringify({ ...body, case_id: verified.caseId }),
        }
    );
    return readPayload(response);
};

export const listDocumentOperations = async (scope, options = {}) => {
    const payload = await request(scope, { action: 'list_document_operations' }, options);
    if (!Array.isArray(payload.operations)) {
        throw new Error('Recent document actions returned an invalid response.');
    }
    return payload.operations.map(normalizeDocumentOperation);
};

export const undoDocumentOperation = async (scope, receiptId, options = {}) => {
    const normalizedReceiptId = String(receiptId ?? '').trim();
    if (!RECEIPT_ID_PATTERN.test(normalizedReceiptId)) {
        throw new Error('That document action could not be verified.');
    }
    return request(scope, {
        action: 'undo_document_operation',
        receipt_id: normalizedReceiptId,
    }, options);
};

export const getDocumentOperationDetails = async (scope, receiptId, options = {}) => {
    const normalizedReceiptId = String(receiptId ?? '').trim();
    const offset = options.offset ?? 0;
    if (!RECEIPT_ID_PATTERN.test(normalizedReceiptId)
        || !Number.isSafeInteger(offset) || offset < 0 || offset > 1000000) {
        throw new Error('That document action could not be verified.');
    }
    const payload = await request(scope, {
        action: 'get_document_operation_details',
        receipt_id: normalizedReceiptId,
        offset,
    }, options);
    if (!Array.isArray(payload.items) || payload.items.length > 100
        || !Number.isSafeInteger(payload.total) || payload.total < 0
        || typeof payload.available !== 'boolean' || typeof payload.truncated !== 'boolean'
        || (payload.available && (payload.items.length === 0 || offset + payload.items.length > payload.total
            || payload.truncated !== (offset + payload.items.length < payload.total)))
        || (!payload.available && (payload.items.length !== 0 || payload.truncated))) {
        throw new Error('Document action details returned an invalid response.');
    }
    return {
        items: payload.items.map(item => {
            if (!item || typeof item.name !== 'string' || typeof item.path !== 'string'
                || typeof item.status !== 'string') {
                throw new Error('Document action details returned an invalid item.');
            }
            return { name: item.name, path: item.path, status: item.status };
        }),
        total: payload.total,
        available: payload.available,
        truncated: payload.truncated,
    };
};

export const getCachedCaseZip = async (scope, options = {}) => {
    const verified = requireScope(scope);
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    const csrfToken = await ensureCaseDbCsrfToken({ expectedUserId: verified.userId });
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    if (!csrfToken) {
        const error = new Error('Your session could not be verified. Sign in again and retry.');
        error.status = 401;
        throw error;
    }

    const endpoint = options.endpoint || caseDbApiEndpoint('documents.php');
    const statusUrl = new URL(endpoint, window.location.origin);
    statusUrl.searchParams.set('case_zip_status', verified.caseId);
    const response = await (options.fetchImpl || fetch)(statusUrl.toString(), {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        signal: options.signal,
        headers: { Accept: 'application/json' },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success !== true
        || !['unavailable', 'building', 'ready'].includes(payload.status)
        || typeof payload.available !== 'boolean') {
        const error = new Error(payload?.message || 'The cached case ZIP is unavailable.');
        error.status = response.status;
        throw error;
    }
    if (payload.status === 'unavailable') {
        if (payload.available) throw new Error('The cached case ZIP returned an invalid response.');
        return { status: 'unavailable', available: false };
    }
    if (payload.status === 'building') {
        const progress = Number(payload.progress);
        const message = String(payload.message ?? '').trim();
        if (payload.available || !Number.isInteger(progress) || progress < 0 || progress > 99
            || !message || message.length > 160) {
            throw new Error('The cached case ZIP returned an invalid response.');
        }
        // The worker message is validation-only. Keep storage paths and other
        // implementation details out of document UI even if a future worker
        // accidentally includes them.
        return {
            status: 'building',
            available: false,
            progress,
            message: 'Creating Case ZIP…',
        };
    }

    const sizeBytes = Number(payload.size_bytes);
    const createdAt = normalizeTimestamp(payload.created_at);
    if (!payload.available || !Number.isSafeInteger(sizeBytes) || sizeBytes < 0
        || !createdAt || !Number.isFinite(Date.parse(createdAt))) {
        throw new Error('The cached case ZIP returned an invalid response.');
    }
    return {
        status: 'ready',
        available: true,
        sizeBytes,
        createdAt,
        downloadUrl: normalizeCachedCaseZipDownloadUrl(
            payload.download_url,
            verified.caseId,
            endpoint
        ),
    };
};

export const resumeDocumentOperation = async (scope, receiptId, options = {}) => {
    const normalizedReceiptId = String(receiptId ?? '').trim();
    if (!RECEIPT_ID_PATTERN.test(normalizedReceiptId)) {
        throw new Error('That document action could not be verified.');
    }
    return request(scope, {
        action: 'resume_document_operation',
        receipt_id: normalizedReceiptId,
    }, options);
};

export const documentOperationScopeKey = ({ caseId, userId }) => (
    JSON.stringify([String(caseId ?? ''), String(userId ?? '')])
);
